import dayjs from 'dayjs';
import { ForwardedRef, forwardRef, useEffect, useMemo } from 'react';
import { ControllerProps, useFormContext, useWatch } from 'react-hook-form';

import { Skeleton } from '../../../components/Skeleton';
import { useValue } from '../../../hooks/useValue';
import { Availability } from '../../../types/base';
import { Field } from '../types';
import { RadioField } from './RadioField';

type SlotFieldProps = Field &
  Omit<Parameters<ControllerProps['render']>['0']['field'], 'ref'>;

type FormAvailability = {
  date: string;
  slots: { label: string; value: string }[];
};

const findSlotsOfSelectedDate = (
  selectedDate: Date,
  availabilities?: FormAvailability[]
) => {
  if (!selectedDate) {
    return null;
  }

  const availability = availabilities?.find((a) =>
    dayjs
      .tz(a?.date, 'Europe/Berlin')
      .isSame(dayjs.tz(selectedDate, 'Europe/Berlin'), 'day')
  );

  return availability?.slots || null;
};

export const SlotField = forwardRef(
  ({ ...field }: SlotFieldProps, ref: ForwardedRef<HTMLInputElement>) => {
    const { formState } = useFormContext();
    const error = formState.errors?.[field.name];
    const [availableDatesRaw, loadingStatus] = useValue<Availability[]>(
      (field.type === 'date-slot' && field.availableDates) || ''
    );
    const selectedDate = useWatch({
      name: (field.type === 'date-slot' && field.dateFieldName) || '',
    });

    const availabilities = useMemo(() => {
      return availableDatesRaw?.map((item) => ({
        date: item.date,
        slots: item.available_slots.map((slot) => ({
          label: slot.formatted_time,
          value: slot.value,
        })),
      }));
    }, [availableDatesRaw]);

    const slotsOfSelectedDate = useMemo(
      () => findSlotsOfSelectedDate(selectedDate, availabilities),
      [availabilities, selectedDate]
    );
    const firstSlotValue = slotsOfSelectedDate?.[0]?.value;
    const canSetInitialSlotValue = Boolean(
      selectedDate &&
        // means: the current slot is not one of the slots of the selected date
        !slotsOfSelectedDate?.find((slot) => slot.value === field.value)
    );

    useEffect(() => {
      if (canSetInitialSlotValue && firstSlotValue) {
        field?.onChange(firstSlotValue);
      }
    }, [canSetInitialSlotValue, firstSlotValue, field]);

    if (field.type !== 'date-slot') {
      return null;
    }

    return (
      <div className="mt-0">
        <h6 className="global-t6-semibold">{field.label}</h6>
        <div className="mt-4">
          <Skeleton
            fullWidth
            height="24px"
            loading={loadingStatus === 'loading'}
          >
            {!error ? (
              <RadioField
                ref={ref}
                radioItems={slotsOfSelectedDate || []}
                variant="circle"
                {...field}
                type="radio"
              />
            ) : (
              <p className="text-red">{error?.message}</p>
            )}
          </Skeleton>
        </div>
      </div>
    );
  }
);
